import { useState } from 'react';
import { formatDate, formatDateTime } from '../../../lib/format';
import type { Industry } from '@dru/types';
import { IndustryCombobox } from '../../../components/IndustryCombobox';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Building2, Hospital, Mail, Pencil, Phone } from 'lucide-react';
import {
  Alert, Button, ConfirmDialog, Detail, DetailHeader, DetailSection, ErrorState, Field, Input,
  SearchSelect, Select, StatusBadge, Steps, type StepSpec, TextField, TextSkeleton, Textarea, cn, useToast,
} from '@dru/ui';
import { ApiError } from '@dru/api-client';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { holds } from '../../../layouts/ConsoleShell';
import { Dialog } from '../../../components/Dialog';
import { Avatar, SOURCE_LABEL, STAGE_LABEL, STATUS_TONE, clean, emailError, localToday, phoneError } from './Leads';
import { useBreadcrumbTitle } from '../../../layouts/BreadcrumbContext';

interface Note { id: string; body: string; createdAt: string; createdBy: string | null }
interface Activity {
  id: string; kind: string; summary: string; occurredAt: string; actorId: string | null;
}
interface LeadDetailShape {
  id: string; kind: 'COMPANY' | 'PARTNER'; source: string; status: string;
  priority: string; organisationName: string;
  contactName: string | null; contactEmail: string | null; contactPhone: string | null;
  website: string | null; city: string | null; state: string | null; pincode: string | null;
  intent: string | null; requirement: string | null; workforceBand: string | null;
  nextFollowUpAt: string | null; lostReason: string | null;
  createdAt: string; convertedAt: string | null;
  assignedTo: { id: string; fullName: string; email: string } | null;
  convertedTenant: { id: string; name: string; status: string } | null;
  convertedPartner: { id: string; code: string; name: string; status: string } | null;
  notes: Note[];
  activities: Activity[];
}

const OPEN = ['NEW', 'CONTACTED', 'QUALIFIED'];
/** Dates the way every other screen prints them: en-IN, with the year. */
const when = (iso: string) => formatDateTime(iso);

/**
 * One conversation.
 *
 * Laid out for the person working it: where the lead stands across the top,
 * then what to do next and the conversation — what they want, notes, the
 * timeline — on the left, and the facts you reach for mid-call — who, how to
 * reach them, who owns it, when to chase — on the right. The timeline is
 * append-only at the database; it is what the next person to pick this up
 * reads. Nothing here is clinical: a lead is a sales conversation, and the
 * words on the page say so.
 *
 * Reading a lead is wider than working it — Support and Finance hold
 * lead.read and nothing else — so every act is offered only with the
 * permission its endpoint checks: lead.update to work it, lead.convert to
 * register it. A reader sees the same page with no buttons on it.
 */
/**
 * Keyed by the record, so moving from one lead to another starts clean: a
 * half-filled form or an open panel belongs to the record it was opened on.
 */
export function LeadDetail() {
  const { id = '' } = useParams();
  return <LeadDetailPage key={id} />;
}

function LeadDetailPage() {
  const { id = '' } = useParams();
  const { toast } = useToast();
  const { principal, permissions } = useAuth();
  const canUpdate = holds(permissions, 'lead.update');
  const canConvert = holds(permissions, 'lead.convert');
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);
  const [losing, setLosing] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [editing, setEditing] = useState(false);

  const lead = useQuery<LeadDetailShape>(() => api.get(`/leads/${id}`), [id]);
  const canAssignOthers = canUpdate && holds(permissions, 'user.read');
  const colleagues = useQuery<{ items: Array<{ id: string; fullName: string; status: string }> }>(
    () => (canAssignOthers
      ? api.get('/admin/platform-users?status=ACTIVE&limit=100')
      : Promise.resolve({ items: [] })),
    [canAssignOthers],
  );
  const l = lead.data;
  useBreadcrumbTitle(l?.organisationName ?? l?.contactName);
  const open = !!l && OPEN.includes(l.status);
  const working = open && canUpdate;
  const mine = !!l?.assignedTo && l.assignedTo.id === principal?.id;

  async function act(key: string, fn: () => Promise<unknown>, done: string) {
    setBusy(key); setError(null);
    try { await fn(); lead.reload(); toast('success', done); }
    catch (e) { setError(errorMessage(e)); }
    finally { setBusy(null); }
  }
  const patch = (key: string, body: Record<string, unknown>, done: string) =>
    act(key, () => api.patch(`/leads/${id}`, body), done);
  /**
   * For a confirmation dialog: the failure has to reach the dialog, which
   * shows it and stays open. Caught here, it landed on the page behind the
   * modal and left the dialog spinning.
   */
  const confirmAct = async (fn: () => Promise<unknown>, done: string, close: () => void) => {
    try { await fn(); } catch (e) { throw new Error(errorMessage(e)); }
    close(); lead.reload(); toast('success', done);
  };

  if (lead.loading) return <TextSkeleton lines={10} />;
  if (lead.error) return <ErrorState title="Could not open this lead" detail={lead.error} onRetry={lead.reload} />;
  if (!l) return null;

  const venue = l.kind === 'PARTNER';
  const nextStatus = l.status === 'NEW' ? 'CONTACTED' : l.status === 'CONTACTED' ? 'QUALIFIED' : null;
  const advance = nextStatus && canUpdate && (
    <Button
      variant={l.assignedTo ? 'primary' : 'secondary'} loading={busy === 'advance'}
      onClick={() => act('advance', () => api.post(`/leads/${id}/status`, { status: nextStatus }), `Marked ${STAGE_LABEL[nextStatus]!.toLowerCase()}.`)}
    >
      Mark {STAGE_LABEL[nextStatus]!.toLowerCase()}
    </Button>
  );
  const pickUp = canUpdate && (
    <Button
      loading={busy === 'owner'}
      onClick={() => patch('owner', { assignedToId: principal?.id }, 'Lead assigned to you.')}
    >
      Assign to me
    </Button>
  );
  const convertButton = canConvert && (
    <Button onClick={() => setConverting(true)}>
      {venue ? 'Convert to partner' : 'Convert to company'} <ArrowRight className="h-3.5 w-3.5" aria-hidden />
    </Button>
  );

  const contact = clean(l.contactName);
  const step = !open ? null
    : !l.assignedTo ? {
        title: 'Give it an owner',
        body: 'Nobody is working this enquiry yet. An enquiry nobody owns is one nobody chases.',
        actions: <>{pickUp}{advance}</>,
      }
    : l.status === 'NEW' ? {
        title: contact ? `Contact ${contact}` : 'Make contact',
        body: l.contactPhone
          ? `Call ${l.contactPhone}${l.contactEmail ? ` or write to ${l.contactEmail}` : ''}, add a note of what was said, then mark it contacted.`
          : l.contactEmail
            ? `Write to ${l.contactEmail}, add a note of what was said, then mark it contacted.`
            : 'No phone or email is recorded. Add one with Edit details before anything else.',
        actions: advance,
      }
    : l.status === 'CONTACTED' ? {
        title: 'Qualify the requirement',
        body: venue
          ? 'Confirm what the hospital or lab can run, its daily capacity and where it is, then mark it qualified.'
          : 'Confirm workforce size, plant locations and the medical schedule, then mark it qualified.',
        actions: advance,
      }
    : {
        title: venue ? 'Convert it to a partner' : 'Convert it to a company',
        body: venue
          ? 'Creates the hospital or lab as onboarding. It is not empanelled — that stays a separate review in the partner network.'
          : 'Creates the company as pending. It is not approved — approval stays a separate step in Companies.',
        actions: convertButton,
      };

  const followUpLate = !!l.nextFollowUpAt && open && new Date(l.nextFollowUpAt).getTime() < Date.now();

  const addNote = () => act('note', async () => { await api.post(`/leads/${id}/notes`, { body: note.trim() }); setNote(''); }, 'Note added.');

  return (
    <div className="space-y-5">
      <DetailHeader
        eyebrow={venue ? 'Hospital / lab enquiry' : 'Company enquiry'}
        title={clean(l.organisationName)}
        badges={(
          <>
            <StatusBadge tone={STATUS_TONE[l.status] ?? 'neutral'}>{STAGE_LABEL[l.status] ?? l.status}</StatusBadge>
            {l.priority === 'HIGH' && <StatusBadge tone="overdue">High priority</StatusBadge>}
          </>
        )}
        subtitle={[
          [clean(l.city), clean(l.state)].filter(Boolean).join(', ') || null,
          `via ${(SOURCE_LABEL[l.source] ?? l.source).toLowerCase()}`,
          `received ${when(l.createdAt)}`,
        ].filter(Boolean).join(' · ')}
        actions={l.status === 'LOST' ? (canUpdate && (
          <Button variant="secondary" onClick={() => setReopening(true)}>Reopen</Button>
        )) : open && (
          <>
            {canUpdate && (
              <Button variant="secondary" onClick={() => setEditing(true)}>
                <Pencil className="h-3.5 w-3.5" aria-hidden /> Edit details
              </Button>
            )}
            {/* Converting before qualifying is allowed; the Next step panel
                offers it once qualified, so it is not offered twice. */}
            {canConvert && l.status !== 'QUALIFIED' && (
              <Button variant="secondary" onClick={() => setConverting(true)}>
                {venue ? 'Convert to partner…' : 'Convert to company…'}
              </Button>
            )}
            {canUpdate && (
              <Button variant="ghost" className="text-danger-600 hover:bg-danger-100" onClick={() => setLosing(true)}>
                Mark lost
              </Button>
            )}
          </>
        )}
      />

      {/* The same journey drawing a medical case and a sample use, so "done",
          "here now" and "stopped" look the same wherever a reader meets them. */}
      <section className="rounded-card border border-neutral-200 bg-white px-5 py-4 shadow-card">
        <Steps steps={stageSteps(l)} label="Lead progress" />
      </section>

      {error && <Alert tone="danger">{error}</Alert>}

      {l.convertedTenant && (
        <Alert tone="success">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>
              Registered as company <strong>{l.convertedTenant.name}</strong>
              {' — '}{l.convertedTenant.status === 'PENDING' ? 'awaiting approval in Companies' : l.convertedTenant.status === 'ACTIVE' ? 'live' : l.convertedTenant.status.toLowerCase()}.
            </span>
            <Link to={`/admin/companies/${l.convertedTenant.id}`} className="font-medium underline hover:text-primary-800">
              Open the company
            </Link>
          </div>
        </Alert>
      )}
      {l.convertedPartner && (
        <Alert tone="success">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>
              Registered as partner <strong>{l.convertedPartner.name}</strong>
              {' — '}{l.convertedPartner.status === 'ONBOARDING' ? 'onboarding, not yet empanelled' : l.convertedPartner.status.toLowerCase()}.
            </span>
            <Link to={`/admin/partners/${l.convertedPartner.id}`} className="font-medium underline hover:text-primary-800">
              Open the partner
            </Link>
          </div>
        </Alert>
      )}

      {l.status === 'LOST' && (
        <Alert tone="warning">
          <strong>Closed as lost</strong>{l.lostReason ? ` — ${l.lostReason}` : ''}.
          {canUpdate && ' Reopen it if the conversation has started again; it goes back to Contacted.'}
        </Alert>
      )}

      {/* The one thing to do, before the facts: first on a phone as well. */}
      {step && (
        <section aria-label="Next step" className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-primary-200 bg-primary-50 p-4">
          <div className="min-w-0 max-w-2xl">
            <p className="text-caption font-medium uppercase tracking-wide text-primary-700">Next step</p>
            <p className="mt-0.5 text-body font-medium text-neutral-900">{step.title}</p>
            <p className="mt-0.5 text-small text-neutral-600">{step.body}</p>
            {l.nextFollowUpAt && (
              <p className={cn('mt-1 text-small', followUpLate ? 'font-medium text-overdue-600' : 'text-neutral-700')}>
                Follow-up promised for {when(l.nextFollowUpAt)}{followUpLate ? ' — overdue' : ''}.
              </p>
            )}
          </div>
          {step.actions && <div className="flex flex-wrap gap-2">{step.actions}</div>}
        </section>
      )}

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,7fr)_minmax(16rem,3fr)]">
        <div className="min-w-0 space-y-4">
          <DetailSection
            title="Requirements"
            action={working && <TextButton onClick={() => setEditing(true)}>Edit</TextButton>}
          >
            {l.intent
              ? <p className="text-body text-neutral-900">{l.intent}</p>
              : (
                <p className="text-small text-neutral-500">
                  No requirement summary recorded.{' '}
                  {working && <TextButton onClick={() => setEditing(true)}>Add summary</TextButton>}
                </p>
              )}
            {l.requirement && (
              <div className="mt-3">
                <p className="text-caption text-neutral-500">Discussion notes / detailed requirements</p>
                <p className="mt-1 whitespace-pre-wrap text-small text-neutral-800">{l.requirement}</p>
              </div>
            )}
          </DetailSection>

          <DetailSection title={`Notes${l.notes.length ? ` (${l.notes.length})` : ''}`}>
            {canUpdate && (
              <div className="space-y-2">
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && note.trim().length >= 2) void addNote();
                  }}
                  placeholder="What was said on the call, what they asked for, what was promised…"
                  aria-label="Add a note"
                  className="w-full"
                />
                <div className="flex items-center justify-between gap-2">
                  <span className="text-caption font-normal text-neutral-500 [@media(pointer:coarse)]:hidden">Ctrl + Enter to add</span>
                  <Button size="sm" disabled={note.trim().length < 2} loading={busy === 'note'} onClick={() => void addNote()}>
                    Add note
                  </Button>
                </div>
              </div>
            )}

            {l.notes.length
              ? (
                <Timeline
                  className={canUpdate ? 'mt-4' : 'mt-3'}
                  items={l.notes.map((n) => ({
                    key: n.id, when: n.createdAt,
                    body: <p className="whitespace-pre-wrap text-small text-neutral-800">{n.body}</p>,
                  }))}
                />
              )
              : <p className={cn('text-small text-neutral-500', canUpdate ? 'mt-4' : 'mt-3')}>No notes yet. The next person to pick this up reads them first.</p>}
          </DetailSection>

          <DetailSection title="Activity">
            {l.activities.length
              ? (
                <Timeline
                  items={l.activities.map((a) => ({
                    key: a.id, when: a.occurredAt,
                    body: <p className="text-small text-neutral-800">{a.summary}</p>,
                  }))}
                />
              )
              : <p className="text-small text-neutral-500">Nothing recorded yet.</p>}
          </DetailSection>
        </div>

        {/* First on a phone: who to ring and who owns it are what you reach
            for mid-call, and below a long timeline they were a scroll away. */}
        <div className="order-first min-w-0 space-y-4 lg:order-none">
          <DetailSection
            title="Contact"
            action={working && <TextButton onClick={() => setEditing(true)}>Edit</TextButton>}
          >
            <dl className="space-y-3">
              <Detail label="Person" value={contact || <NotRecorded />} />
              <Detail
                label="Phone"
                value={l.contactPhone
                  ? <a href={`tel:${l.contactPhone}`} className="inline-flex items-center gap-1.5 py-0.5 text-primary-700 hover:underline"><Phone className="h-3.5 w-3.5" aria-hidden />{l.contactPhone}</a>
                  : <NotRecorded />}
              />
              <Detail
                label="Email"
                value={l.contactEmail
                  ? <a href={`mailto:${l.contactEmail}`} className="inline-flex items-center gap-1.5 break-all py-0.5 text-primary-700 hover:underline"><Mail className="h-3.5 w-3.5 shrink-0" aria-hidden />{l.contactEmail}</a>
                  : <NotRecorded />}
              />
              <Detail label="Location" value={[clean(l.city), clean(l.state), l.pincode].filter(Boolean).join(', ') || <NotRecorded />} />
              <Detail label={venue ? 'Daily capacity' : 'Workforce'} value={l.workforceBand ? `${l.workforceBand}${venue ? '' : ' employees'}` : <NotRecorded />} />
              {l.website && <Detail label="Website" value={<a href={/^https?:/.test(l.website) ? l.website : `https://${l.website}`} target="_blank" rel="noreferrer" className="break-all text-primary-700 hover:underline">{l.website}</a>} />}
            </dl>
          </DetailSection>

          {/* Who has it, how much it matters and when it is next chased: the
              three settings that make it somebody's task, in one place. */}
          <DetailSection title="Ownership and follow-up">
            <dl className="space-y-4">
              <div>
                <dt className="text-caption font-medium text-neutral-600">Owner</dt>
                <dd className="mt-1 space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    {l.assignedTo
                      ? <span className="inline-flex items-center gap-2 text-small text-neutral-900"><Avatar name={l.assignedTo.fullName} />{l.assignedTo.fullName}{mine ? ' (you)' : ''}</span>
                      : <StatusBadge tone="dueSoon">Unassigned</StatusBadge>}
                    {working && (mine
                      ? <Button size="sm" variant="secondary" loading={busy === 'owner'} onClick={() => patch('owner', { assignedToId: null }, 'Unassigned.')}>Unassign</Button>
                      : <Button size="sm" variant="secondary" loading={busy === 'owner'} onClick={() => patch('owner', { assignedToId: principal?.id }, 'Lead assigned to you.')}>{l.assignedTo ? 'Take it over' : 'Assign to me'}</Button>)}
                  </div>
                  {working && canAssignOthers && (colleagues.data?.items.length ?? 0) > 0 && (
                    <SearchSelect
                      className="w-full" aria-label="Hand it to a colleague"
                      value=""
                      onChange={(v) => {
                        const who = colleagues.data?.items.find((c) => c.id === v);
                        if (who) void patch('owner', { assignedToId: who.id }, `Assigned to ${who.fullName}.`);
                      }}
                      placeholder="Hand it to a colleague…"
                      searchPlaceholder="Search colleagues…"
                      options={colleagues.data!.items.filter((c) => c.id !== l.assignedTo?.id && c.id !== principal?.id).map((c) => ({ value: c.id, label: c.fullName }))}
                    />
                  )}
                </dd>
              </div>

              <div>
                <dt className="text-caption font-medium text-neutral-600">{working ? <label htmlFor="lead-priority">Priority</label> : 'Priority'}</dt>
                <dd className="mt-1">
                  {working
                    ? (
                      <Select
                        id="lead-priority" value={l.priority} disabled={busy === 'priority'}
                        onChange={(e) => patch('priority', { priority: e.target.value }, 'Priority updated.')}
                        className="w-full"
                      >
                        <option value="HIGH">High</option>
                        <option value="MEDIUM">Medium</option>
                        <option value="LOW">Low</option>
                      </Select>
                    )
                    : <span className="text-small text-neutral-900">{PRIORITY_LABEL[l.priority] ?? l.priority}</span>}
                </dd>
              </div>

              <div>
                <dt className="text-caption font-medium text-neutral-600">Next follow-up</dt>
                <dd className="mt-1">
                  {working
                    ? (
                      <FollowUp
                        key={l.nextFollowUpAt ?? 'none'}
                        at={l.nextFollowUpAt}
                        late={followUpLate}
                        busy={busy === 'follow'}
                        onSave={(date) => patch('follow', { nextFollowUpAt: new Date(`${date}T09:00:00`).toISOString() }, 'Follow-up date set.')}
                        onClear={() => patch('follow', { nextFollowUpAt: null }, 'Follow-up cleared.')}
                      />
                    )
                    : (
                      <span className={cn('text-small', followUpLate ? 'font-medium text-overdue-600' : l.nextFollowUpAt && open ? 'text-neutral-900' : 'text-neutral-500')}>
                        {!open ? 'None — the lead is closed'
                          : l.nextFollowUpAt ? `${formatDate(l.nextFollowUpAt)}${followUpLate ? ' · overdue' : ''}` : 'No date promised'}
                      </span>
                    )}
                </dd>
              </div>
            </dl>
          </DetailSection>
        </div>
      </div>

      {editing && (
        <EditDetailsDialog
          lead={l}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); lead.reload(); toast('success', 'Details saved.'); }}
        />
      )}

      {losing && (
        <ConfirmDialog
          title="Mark this lead lost?"
          what={<>The enquiry from <strong>{clean(l.organisationName)}</strong> leaves the open inbox and stops being anybody’s to work.</>}
          affects="Its notes and timeline are kept, and it can be reopened later. A new enquiry from the same email or phone opens a fresh lead."
          confirmLabel="Mark lost"
          tone="danger"
          requireReason
          reasonLabel="Why was it lost?"
          reasonHint="At least five characters. Written on the lead’s timeline."
          onCancel={() => setLosing(false)}
          onConfirm={(reason) => confirmAct(async () => {
            if (reason.trim().length < 5) throw new Error('Say why in at least five characters.');
            await api.post(`/leads/${id}/status`, { status: 'LOST', reason });
          }, 'Closed as lost.', () => setLosing(false))}
        />
      )}

      {reopening && (
        <ConfirmDialog
          title="Reopen this lead?"
          what="It goes back to Contacted and into the open inbox."
          affects="The reason is written on the timeline. If another open enquiry already has this email or phone, reopening is refused and names it."
          confirmLabel="Reopen"
          requireReason
          reasonLabel="Why is it being reopened?"
          reasonHint="At least five characters. Written on the lead’s timeline."
          onCancel={() => setReopening(false)}
          onConfirm={(reason) => confirmAct(async () => {
            if (reason.trim().length < 5) throw new Error('Say why in at least five characters.');
            await api.post(`/leads/${id}/reopen`, { reason });
          }, 'Reopened.', () => setReopening(false))}
        />
      )}

      {converting && (
        <ConvertDialog
          lead={l}
          onClose={() => setConverting(false)}
          onDone={(message) => {
            setConverting(false);
            lead.reload();
            toast('success', message);
          }}
        />
      )}
    </div>
  );
}

const PRIORITY_LABEL: Record<string, string> = { HIGH: 'High', MEDIUM: 'Medium', LOW: 'Low' };

/**
 * The promised follow-up date, set on purpose.
 *
 * It used to save on every change of the date field, and a date typed from
 * the keyboard is several changes — "0002-09-25" on the way to "2026-09-25"
 * — each one written to the lead and its timeline. The date is now chosen,
 * then set, with the common choices one click away.
 */
function FollowUp({ at, late, busy, onSave, onClear }: {
  at: string | null;
  late: boolean;
  busy: boolean;
  onSave: (date: string) => void;
  onClear: () => void;
}) {
  const current = at ? toLocalDate(at) : '';
  const [date, setDate] = useState(current);
  const min = localToday();
  const dirty = date !== current;
  const tooEarly = !!date && date < min;
  const inDays = (n: number) => {
    const d = new Date(); d.setDate(d.getDate() + n);
    return toLocalDate(d.toISOString());
  };
  return (
    <div className="space-y-2">
      {at && !dirty && (
        <p className={cn('text-small', late ? 'font-medium text-overdue-600' : 'text-neutral-900')}>
          {formatDate(at)}{late ? ' · overdue — the promised call has not happened' : ''}
        </p>
      )}
      {!at && !dirty && <p className="text-small text-neutral-500">No date promised yet.</p>}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="date" aria-label="Follow-up date" min={min} value={date}
          onChange={(e) => setDate(e.target.value)} invalid={tooEarly} className="min-w-0 flex-1"
        />
        {dirty && date && (
          <Button size="sm" loading={busy} disabled={tooEarly} onClick={() => onSave(date)}>Set</Button>
        )}
        {!dirty && at && (
          <Button size="sm" variant="secondary" loading={busy} onClick={onClear}>Clear</Button>
        )}
      </div>
      {tooEarly && <p role="alert" className="text-caption font-normal text-danger-600">Choose today or a later date.</p>}
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Quick follow-up dates">
        {[['Tomorrow', 1], ['In 3 days', 3], ['Next week', 7]].map(([label, n]) => (
          <button
            key={label as string} type="button" disabled={busy}
            onClick={() => onSave(inDays(n as number))}
            className="min-h-6 rounded-badge border border-neutral-200 px-2 py-0.5 text-caption [@media(pointer:coarse)]:min-h-10 [@media(pointer:coarse)]:px-3 text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** A timestamp's calendar day where the reader is, as a date input wants it. */
function toLocalDate(iso: string) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function TextButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="-my-1 inline-flex min-h-6 min-w-6 items-center justify-center rounded-btn px-1 text-caption font-medium text-primary-600 hover:text-primary-700">
      {children}
    </button>
  );
}

/** A blank that says it is blank, at a rank that can be read. */
function NotRecorded() {
  return <span className="text-neutral-500">Not recorded</span>;
}

const FLOW = ['NEW', 'CONTACTED', 'QUALIFIED', 'CONVERTED'] as const;

/**
 * Where the lead stands, as the journey the shared Steps primitive draws.
 *
 * A lost lead stopped somewhere on the way; the record does not say where,
 * so the stop is drawn on the last step with the reason, rather than
 * claiming a stage it may never have reached.
 */
function stageSteps(l: LeadDetailShape): StepSpec[] {
  const lost = l.status === 'LOST';
  const at = FLOW.indexOf(l.status as (typeof FLOW)[number]);
  return FLOW.map((s, i) => {
    const last = i === FLOW.length - 1;
    const state: StepSpec['state'] = lost
      ? (last ? 'blocked' : 'upcoming')
      : l.status === 'CONVERTED' || i < at ? 'done'
        : i === at ? 'current' : 'upcoming';
    return {
      key: s,
      // A lost lead's last stop is "Lost", not a "Converted" drawn in
      // warning colours that read at a glance as if it had converted.
      label: lost && last ? 'Lost' : STAGE_LABEL[s] ?? s,
      state,
      detail: lost && last ? (l.lostReason ?? undefined)
        : s === 'NEW' ? `Received ${when(l.createdAt)}`
          : s === 'CONVERTED' && l.convertedAt ? when(l.convertedAt)
            : undefined,
    };
  });
}

/**
 * Notes and activity, on one rail.
 *
 * The same drawing for both: a marker, what happened, when. Newest first is
 * how the API sends them and how a person reads a conversation they are
 * picking up mid-way.
 */
function Timeline({ items, className }: {
  items: Array<{ key: string; when: string; body: React.ReactNode }>;
  className?: string;
}) {
  return (
    <ol className={cn('space-y-4 border-l-2 border-neutral-200 pl-4', className)}>
      {items.map((it) => (
        <li key={it.key} className="relative">
          <span aria-hidden className="absolute -left-[23px] top-1.5 h-3 w-3 rounded-full border-2 border-neutral-400 bg-white" />
          <p className="text-caption font-normal text-neutral-500">{when(it.when)}</p>
          <div className="mt-0.5">{it.body}</div>
        </li>
      ))}
    </ol>
  );
}

/**
 * Correcting what we know about the enquiry.
 *
 * Only changed fields are sent. Emptying a field clears it — a wrong phone
 * number is removed, not left behind. Grouped as the page reads: who they
 * are, what they want, how to reach them.
 */
function EditDetailsDialog({ lead, onClose, onSaved }: {
  lead: LeadDetailShape; onClose: () => void; onSaved: () => void;
}) {
  const venue = lead.kind === 'PARTNER';
  const initial = {
    organisationName: lead.organisationName, contactName: lead.contactName ?? '',
    contactPhone: lead.contactPhone ?? '', contactEmail: lead.contactEmail ?? '',
    city: lead.city ?? '', state: lead.state ?? '', pincode: lead.pincode ?? '',
    workforceBand: lead.workforceBand ?? '', website: lead.website ?? '',
    intent: lead.intent ?? '', requirement: lead.requirement ?? '',
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [k]: v }));
  const changed = Object.fromEntries(
    (Object.keys(form) as Array<keyof typeof form>)
      .filter((k) => form[k].trim() !== initial[k].trim())
      .map((k) => [k, form[k].trim()]),
  );
  const errors = {
    organisationName: form.organisationName.trim().length < 2 ? 'Enter the organisation’s name (at least 2 characters)' : null,
    contactPhone: phoneError(form.contactPhone),
    contactEmail: emailError(form.contactEmail),
    pincode: form.pincode.trim() && !/^[0-9]{6}$/.test(form.pincode.trim()) ? 'Six digits' : null,
  };
  const invalid = Object.values(errors).some(Boolean);

  async function save() {
    if (invalid || !Object.keys(changed).length) return;
    setBusy(true); setError(null);
    try { await api.patch(`/leads/${lead.id}`, changed); onSaved(); }
    catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  return (
    <Dialog
      title="Edit enquiry details" onClose={onClose} wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} loading={busy} disabled={!Object.keys(changed).length || invalid}>Save changes</Button>
        </>
      }
    >
      <form className="mt-4 space-y-5" onSubmit={(e) => { e.preventDefault(); void save(); }} noValidate>
        <section aria-labelledby="edit-what" className="space-y-3">
          <h3 id="edit-what" className="text-caption font-semibold uppercase tracking-wider text-neutral-700">Who and what</h3>
          <TextField
            label={venue ? 'Hospital or lab name' : 'Company name'} value={form.organisationName}
            onChange={set('organisationName')} required autoFocus error={errors.organisationName}
          />
          <div className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <TextField label="Requirement in a line" value={form.intent} onChange={set('intent')}
              placeholder={venue ? 'e.g. Wants to run pre-employment panels' : 'e.g. Annual statutory medicals for the plant'} />
            <TextField label={venue ? 'Daily capacity' : 'Workforce size'} value={form.workforceBand} onChange={set('workforceBand')}
              placeholder={venue ? 'e.g. 50 a day' : 'e.g. 100–500'} />
          </div>
          <Field label="Discussion notes / detailed requirements">
            {(p) => (
              <Textarea {...p} value={form.requirement} onChange={(e) => set('requirement')(e.target.value)}
                placeholder="Tests they asked about, sites, timeline, who decides…" rows={3} />
            )}
          </Field>
        </section>
        <section aria-labelledby="edit-reach" className="space-y-3 border-t border-neutral-200 pt-4">
          <h3 id="edit-reach" className="text-caption font-semibold uppercase tracking-wider text-neutral-700">How to reach them</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField label="Contact person" value={form.contactName} onChange={set('contactName')} />
            <TextField label="Phone" value={form.contactPhone} onChange={set('contactPhone')} inputMode="tel" type="tel" error={errors.contactPhone} />
            <TextField label="Email" value={form.contactEmail} onChange={set('contactEmail')} type="email" error={errors.contactEmail} />
            <TextField label="Website" value={form.website} onChange={set('website')} />
            <TextField label="City" value={form.city} onChange={set('city')} />
            <TextField label="State" value={form.state} onChange={set('state')} />
            <TextField label="Pincode" value={form.pincode} onChange={set('pincode')} inputMode="numeric" error={errors.pincode} />
          </div>
        </section>
        {error && <Alert tone="danger">{error}</Alert>}
      </form>
    </Dialog>
  );
}

/**
 * The lead becomes a real registration.
 *
 * A company is registered the way "Register a company" registers one: live
 * at once, with the contact below as its administrator, who gets the welcome
 * with their sign-in link (flow audit 1.6). The administrator is filled from
 * the lead and corrected here, so nobody retypes them on a second Approve.
 * Somebody who may not activate companies still creates a pending one, and a
 * venue still lands onboarding. The dialog says which before the button is
 * pressed, not after.
 */
function ConvertDialog({ lead, onClose, onDone }: {
  lead: LeadDetailShape;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const venue = lead.kind === 'PARTNER';
  const live = !venue && holds(useAuth().permissions, 'tenant.activate');
  const [name, setName] = useState(clean(lead.organisationName));
  const [code, setCode] = useState('');
  const [partnerType, setPartnerType] = useState('HOSPITAL');
  const [contactName, setContactName] = useState(clean(lead.contactName));
  const [contactEmail, setContactEmail] = useState(lead.contactEmail ?? '');
  const [contactPhone, setContactPhone] = useState(() => {
    const digits = (lead.contactPhone ?? '').replace(/\D/g, '').slice(-10);
    return /^[6-9]\d{9}$/.test(digits) ? digits : '';
  });
  const [pincode, setPincode] = useState(lead.pincode ?? '');
  const [industry, setIndustry] = useState<Industry | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [tried, setTried] = useState(false);

  const errors = {
    name: name.trim().length < 2 ? 'Enter the name (at least 2 characters)' : null,
    code: venue && code.trim().length < 2 ? 'Enter a code of at least 2 characters' : null,
    contactName: live && contactName.trim().length < 2 ? 'Enter the administrator’s name' : null,
    contactEmail: live && !contactEmail.trim() ? 'Enter the email the sign-in link goes to' : emailError(contactEmail),
    contactPhone: live && contactPhone && !/^[6-9]\d{9}$/.test(contactPhone) ? 'Enter a valid 10-digit mobile number' : null,
    pincode: live && !/^\d{6}$/.test(pincode) ? 'Enter the 6-digit pincode — the first plant is created from it' : null,
  };
  const invalid = Object.values(errors).some(Boolean);
  const shown = (k: keyof typeof errors) => (tried ? errors[k] : null) ?? serverErrors[k] ?? null;

  async function submit() {
    setTried(true);
    if (invalid) return;
    setBusy(true); setError(null); setServerErrors({});
    try {
      const r = await api.post<{ tenant?: { administrator?: { email: string }; invitation?: { failed: string } | object | null } }>(`/leads/${lead.id}/convert`, {
        name: name.trim(),
        ...(contactEmail.trim() ? { contactEmail: contactEmail.trim().toLowerCase() } : {}),
        ...(venue ? { code: code.trim().toUpperCase(), partnerType } : {}),
        ...(!venue && industry ? { industryId: industry.id } : {}),
        ...(!venue && contactName.trim() ? { contactName: contactName.trim() } : {}),
        ...(!venue && contactPhone ? { contactPhone } : {}),
        ...(!venue && pincode ? { pincode } : {}),
      }, crypto.randomUUID());
      const org = name.trim();
      const failed = r.tenant?.invitation && 'failed' in r.tenant.invitation ? (r.tenant.invitation as { failed: string }).failed : null;
      onDone(venue ? `${org} is registered and awaiting empanelment.`
        : !r.tenant?.administrator ? `${org} is registered and awaiting approval.`
        : failed ? `${org} is live, but the welcome email was not sent: ${failed} Resend it from the company page.`
        : `${org} is live. The welcome email with the sign-in link went to ${r.tenant.administrator.email}.`);
    } catch (e) {
      if (e instanceof ApiError && e.hasFieldErrors) setServerErrors(e.fieldErrors);
      setError(errorMessage(e)); setBusy(false);
    }
  }

  return (
    <Dialog
      title={venue ? 'Convert to a partner' : 'Convert to a company'} onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} loading={busy}>
            {venue ? 'Create onboarding partner' : live ? 'Create company' : 'Create pending company'}
          </Button>
        </>
      }
    >
      {/* What this does and does not do, before anything is typed: an
          operator must know whether the company can now sign in. */}
      <div className="mt-3 flex items-start gap-2 rounded-input border border-info-600/25 bg-info-100 px-3 py-2 text-small text-neutral-800">
        {venue ? <Hospital className="mt-0.5 h-4 w-4 shrink-0 text-info-600" aria-hidden />
               : <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-info-600" aria-hidden />}
        <span>
          {venue
            ? <>Creates the partner as <strong>onboarding</strong>. It is not empanelled and receives no cases until it is reviewed in the partner network.</>
            : live
              ? <>The company goes <strong>live</strong> straight away. The contact below becomes its administrator and gets the welcome email with a sign-in link.</>
              : <>Creates the company as <strong>pending</strong>. Approval stays a separate review in Companies.</>}
          {' '}The lead is marked converted and keeps its history.
        </span>
      </div>

      <form className="mt-4 space-y-4" onSubmit={(e) => { e.preventDefault(); void submit(); }} noValidate>
        <TextField
          label={venue ? 'Partner name' : 'Registered company name'}
          value={name} onChange={setName} required autoFocus
          info="As it should appear on certificates and invoices."
          error={shown('name')}
        />

        {venue && (
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Partner code" value={code} onChange={setCode} mono required
              placeholder="HOSP-01" hint="Short and unique; the registry is keyed on it."
              error={shown('code')}
            />
            <Field label="What it is">
              {(p) => (
                <Select {...p} value={partnerType} onChange={(e) => setPartnerType(e.target.value)}>
                  <option value="HOSPITAL">Hospital</option>
                  <option value="LABORATORY">Laboratory</option>
                  <option value="BOTH">Hospital and laboratory</option>
                </Select>
              )}
            </Field>
          </div>
        )}

        {!venue && (
          <div className="grid gap-3 sm:grid-cols-2">
            <IndustryCombobox
              label="Industry (optional)"
              value={industry} onChange={setIndustry}
              info="Suggests health packages in the medical configuration. Can be set later."
            />
            <TextField
              label="Pincode" value={pincode} required={live} mono inputMode="numeric"
              onChange={(v) => setPincode(v.replace(/\D/g, '').slice(0, 6))}
              info="The first plant is created from it, so HR can add employees straight away."
              error={shown('pincode')}
            />
          </div>
        )}

        {venue ? (
          <TextField
            label="Contact email" value={contactEmail} onChange={setContactEmail}
            type="email" error={shown('contactEmail')}
          />
        ) : (
          <fieldset className="grid gap-3 sm:grid-cols-2">
            <legend className="mb-1 text-small font-semibold text-neutral-900">Administrator</legend>
            <TextField label="Name" value={contactName} onChange={setContactName} required={live} error={shown('contactName')} />
            <TextField
              label="Email" value={contactEmail} onChange={setContactEmail} type="email" required={live}
              info={live ? 'The welcome email with the sign-in link goes here.' : undefined}
              error={shown('contactEmail')}
            />
            <TextField
              label="Mobile (optional)" value={contactPhone} mono inputMode="numeric"
              onChange={(v) => setContactPhone(v.replace(/\D/g, '').slice(0, 10))}
              error={shown('contactPhone')}
            />
          </fieldset>
        )}

        {error && <Alert tone="danger">{error}</Alert>}
      </form>
    </Dialog>
  );
}
